/**
 * CodeEditorSidebar — Full-featured code editor for the right sidebar.
 *
 * Uses CodeMirror 6 via @uiw/react-codemirror with theme support.
 *
 * Install:
 *   npm install @uiw/react-codemirror @uiw/codemirror-themes-all
 *   npm install @codemirror/lang-javascript @codemirror/lang-python
 *   npm install @codemirror/lang-html @codemirror/lang-css @codemirror/lang-json
 *   npm install @codemirror/lang-markdown @codemirror/lang-sql @codemirror/lang-xml
 *   npm install @codemirror/lang-java @codemirror/lang-cpp @codemirror/lang-rust
 *   npm install @codemirror/lang-php @codemirror/lang-go
 *
 * Props:
 *   codeBlocks: [{ id, language, code, label }]  — code blocks from the response
 *   activeBlockId: string — which block is currently shown
 *   onChangeBlock: (id) => void — switch between blocks
 *   onCodeChange: (id, newCode) => void — user edits code
 *   onClose: () => void — close the sidebar
 *   isStreaming: boolean — if true, editor is read-only and updates live
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import {
  X,
  Copy,
  Check,
  Download,
  ChevronDown,
  Save,
  Palette,
} from "lucide-react";
import CodeMirror from "@uiw/react-codemirror";

// Themes
import {
  dracula,
  solarizedDark,
  nord,
  githubDark,
  githubLight,
  tokyoNight,
  tokyoNightStorm,
  vscodeDark,
  androidstudio,
  atomone,
  monokai,
  materialDark,
  copilot,
  aura,
  sublime,
} from "@uiw/codemirror-themes-all";

// Language support
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { sql } from "@codemirror/lang-sql";
import { xml } from "@codemirror/lang-xml";
import { java } from "@codemirror/lang-java";
import { cpp } from "@codemirror/lang-cpp";
import { rust } from "@codemirror/lang-rust";
import { php } from "@codemirror/lang-php";
import { go } from "@codemirror/lang-go";

/* ── Theme map ── */
const THEMES = {
  Dracula: dracula,
  "Tokyo Night": tokyoNight,
  "Tokyo Night Storm": tokyoNightStorm,
  "VS Code Dark": vscodeDark,
  Nord: nord,
  Monokai: monokai,
  "Material Dark": materialDark,
  Copilot: copilot,
  Aura: aura,
  Sublime: sublime,
  "Solarized Dark": solarizedDark,
  "GitHub Dark": githubDark,
  "GitHub Light": githubLight,
  "Android Studio": androidstudio,
  "Atom One": atomone,
};

const THEME_KEYS = Object.keys(THEMES);

/* ── Language extension map ── */
const LANG_EXTENSIONS = {
  javascript: () => javascript({ jsx: true, typescript: false }),
  js: () => javascript({ jsx: true }),
  jsx: () => javascript({ jsx: true }),
  typescript: () => javascript({ jsx: true, typescript: true }),
  ts: () => javascript({ jsx: true, typescript: true }),
  tsx: () => javascript({ jsx: true, typescript: true }),
  python: () => python(),
  py: () => python(),
  html: () => html(),
  css: () => css(),
  json: () => json(),
  markdown: () => markdown(),
  md: () => markdown(),
  sql: () => sql(),
  xml: () => xml(),
  java: () => java(),
  cpp: () => cpp(),
  c: () => cpp(),
  "c++": () => cpp(),
  rust: () => rust(),
  rs: () => rust(),
  php: () => php(),
  go: () => go(),
  golang: () => go(),
  bash: () => [],
  shell: () => [],
  yaml: () => [],
  yml: () => [],
  toml: () => [],
  dockerfile: () => [],
  text: () => [],
};

function getLanguageExtension(lang) {
  const fn = LANG_EXTENSIONS[(lang || "").toLowerCase()];
  if (fn) {
    const ext = fn();
    return Array.isArray(ext) ? ext : [ext];
  }
  return [];
}

/* ── File extension map for downloads ── */
const FILE_EXTENSIONS = {
  javascript: "js",
  js: "js",
  jsx: "jsx",
  typescript: "ts",
  ts: "ts",
  tsx: "tsx",
  python: "py",
  py: "py",
  html: "html",
  css: "css",
  json: "json",
  markdown: "md",
  md: "md",
  sql: "sql",
  xml: "xml",
  java: "java",
  cpp: "cpp",
  c: "c",
  rust: "rs",
  rs: "rs",
  php: "php",
  go: "go",
  bash: "sh",
  shell: "sh",
  yaml: "yaml",
  yml: "yml",
  toml: "toml",
  dockerfile: "Dockerfile",
};

/* ── Component ── */
const CodeEditorSidebar = ({
  codeBlocks = [],
  activeBlockId,
  onChangeBlock,
  onCodeChange,
  onClose,
  isStreaming = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [blockSelectorOpen, setBlockSelectorOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(() => {
    try {
      return localStorage.getItem("codeEditorTheme") || "Dracula";
    } catch {
      return "Dracula";
    }
  });

  const activeBlock =
    codeBlocks.find((b) => b.id === activeBlockId) || codeBlocks[0];
  const theme = THEMES[selectedTheme] || dracula;

  const extensions = useMemo(
    () => getLanguageExtension(activeBlock?.language),
    [activeBlock?.language],
  );

  const handleThemeChange = (name) => {
    setSelectedTheme(name);
    localStorage.setItem("codeEditorTheme", name);
    setThemePickerOpen(false);
  };

  const handleCopy = useCallback(async () => {
    if (!activeBlock) return;
    await navigator.clipboard.writeText(activeBlock.code).catch(console.error);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [activeBlock]);

  const handleDownload = useCallback(() => {
    if (!activeBlock) return;
    const ext =
      FILE_EXTENSIONS[(activeBlock.language || "").toLowerCase()] || "txt";
    const blob = new Blob([activeBlock.code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `code.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }, [activeBlock]);

  const handleSave = useCallback(() => {
    // TODO Phase 2F: save to posts_col via storeUserCode API
    // group: "private", target_type: "user_generated_code"
    console.log("Save code — TODO: wire to backend", activeBlock);
  }, [activeBlock]);

  if (!activeBlock) return null;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-zinc-950">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 dark:border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          {/* Block selector (when multiple blocks) */}
          {codeBlocks.length > 1 ? (
            <div className="relative">
              <button
                onClick={() => setBlockSelectorOpen(!blockSelectorOpen)}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-[12px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
              >
                <span className="truncate max-w-[140px]">
                  {activeBlock.label || activeBlock.language || "code"}
                </span>
                <ChevronDown className="h-3 w-3 shrink-0" />
              </button>
              {blockSelectorOpen && (
                <div className="absolute top-full left-0 mt-1 min-w-[180px] rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg z-20">
                  {codeBlocks.map((block) => (
                    <button
                      key={block.id}
                      onClick={() => {
                        onChangeBlock?.(block.id);
                        setBlockSelectorOpen(false);
                      }}
                      className={[
                        "w-full text-left px-3 py-1.5 text-[12px] transition-colors",
                        block.id === activeBlockId
                          ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium"
                          : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/50",
                      ].join(" ")}
                    >
                      <span className="text-zinc-400 dark:text-zinc-600 mr-1.5">
                        {block.language || "code"}
                      </span>
                      {block.label || `Block ${codeBlocks.indexOf(block) + 1}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <span className="text-[12px] font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
              {activeBlock.language || "code"}
            </span>
          )}

          {isStreaming && (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-500">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              streaming
            </span>
          )}
        </div>

        <div className="flex items-center gap-0.5">
          {/* Theme picker */}
          <div className="relative">
            <button
              onClick={() => setThemePickerOpen(!themePickerOpen)}
              title="Change theme"
              className="p-1.5 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            >
              <Palette className="h-3.5 w-3.5" />
            </button>
            {themePickerOpen && (
              <div className="absolute top-full right-0 mt-1 w-48 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg z-20 max-h-64 overflow-y-auto">
                {THEME_KEYS.map((name) => (
                  <button
                    key={name}
                    onClick={() => handleThemeChange(name)}
                    className={[
                      "w-full text-left px-3 py-1.5 text-[12px] transition-colors",
                      name === selectedTheme
                        ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium"
                        : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/50",
                    ].join(" ")}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Save */}
          <button
            onClick={handleSave}
            title="Save code"
            className="p-1.5 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <Save className="h-3.5 w-3.5" />
          </button>

          {/* Download */}
          <button
            onClick={handleDownload}
            title="Download file"
            className="p-1.5 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
          </button>

          {/* Copy */}
          <button
            onClick={handleCopy}
            title="Copy code"
            className="p-1.5 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>

          {/* Close */}
          <button
            onClick={onClose}
            title="Close editor"
            className="p-1.5 rounded text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* ── Editor ── */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <CodeMirror
          value={activeBlock.code}
          theme={theme}
          extensions={extensions}
          onChange={(value) => {
            if (!isStreaming) {
              onCodeChange?.(activeBlock.id, value);
            }
          }}
          readOnly={isStreaming}
          basicSetup={{
            lineNumbers: true,
            highlightActiveLine: !isStreaming,
            highlightActiveLineGutter: !isStreaming,
            foldGutter: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: false,
            indentOnInput: true,
          }}
          style={{ height: "100%", fontSize: "13px" }}
          className="h-full [&_.cm-editor]:h-full [&_.cm-scroller]:!overflow-auto"
        />
      </div>
    </div>
  );
};

export default CodeEditorSidebar;
